import type { CompanionPanelAsk } from '@/hooks/actions/use-companion-panel-ask';
import { COMPANION_COMPOSER_ATTRIBUTE } from '@/components/companion/feed/companion-panel-ids';
import { Box, Stack } from '@chakra-ui/react';
import { Composer } from '@fluenceur/agent-blocks';
import { useEffect, useState } from 'react';
import { CompanionPanelNotice } from './CompanionPanelNotice';

interface CompanionPanelComposerProps extends CompanionPanelAsk {
  /** The page being read, or undefined for a page the Companion has no name for. */
  pageName?: string;
  autoFocus: boolean;
}

/**
 * Where the brand asks. Nothing typed is lost: Enter does nothing while an
 * answer is being written, and a question the server refused comes back into
 * the field, unless the brand has already started another one.
 */
export const CompanionPanelComposer = ({
  ask,
  stop,
  isAnswering,
  notice,
  pageName,
  autoFocus,
}: CompanionPanelComposerProps) => {
  const [draft, setDraft] = useState('');

  useEffect(() => {
    if (notice) setDraft((current) => current || notice.question);
  }, [notice]);

  return (
    <Stack gap="2" {...{ [COMPANION_COMPOSER_ATTRIBUTE]: '' }}>
      {/* Mounted empty, so a screen reader hears the notice when it appears. */}
      <Box role="status" aria-live="polite" _empty={{ display: 'none' }}>
        {notice ? <CompanionPanelNotice kind={notice.kind} /> : null}
      </Box>
      <Composer
        value={draft}
        onValueChange={setDraft}
        onSubmit={ask}
        isAnswering={isAnswering}
        onStop={stop}
        pageName={pageName}
        autoFocus={autoFocus}
      />
    </Stack>
  );
};
